import { DeviceMetricTypeCode } from '@quromedical/fhir-common';
import { z } from 'zod';
import { DisplayReferenceSchema, UUIDSchema } from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import { PaginatedBundleSchema } from '../domain/schema';
import { ObservationSchema } from '../observation/schema';

export enum Command {
  PatchCalibrate = 'patch-calibrate',
}

const DeviceMetricCalibrationType = [
  'unspecified',
  'offset',
  'gain',
  'two-point',
] as const;
const DeviceMetricCategory = [
  'unspecified',
  'measurement',
  'setting',
  'calculation',
] as const;
const DeviceMetricCalibrationState = [
  'not-calibrated',
  'calibration-required',
  'calibrated',
  'unspecified',
] as const;

export const CommandSchema = z.nativeEnum(Command);
export const DeviceMetricCategorySchema = z.enum(DeviceMetricCategory);
export const DeviceMetricCalibrationStateSchema = z.enum(
  DeviceMetricCalibrationState,
);
export const DeviceMetricCalibrationTypeSchema = z.enum(
  DeviceMetricCalibrationType,
);
export const DeviceMetricTypeSchema = z.nativeEnum(DeviceMetricTypeCode);

export const DeviceCommandSchema = z.object({
  command: CommandSchema,
  state: DeviceMetricCalibrationStateSchema,
  type: DeviceMetricCalibrationTypeSchema,
});

export const DeviceMetricSchema = z.object({
  time: z.string(),
  type: DeviceMetricTypeSchema,
  category: DeviceMetricCategorySchema,
  command: DeviceCommandSchema,
  measurement: ObservationSchema,
  device: DisplayReferenceSchema,
});

const PartialDeviceMetricSchema = DeviceMetricSchema.extend({
  measurement: ObservationSchema.partial(),
  command: DeviceCommandSchema.partial(),
}).partial();

export const DeviceMetricGetResponseSchema = PartialDeviceMetricSchema.extend({
  id: UUIDSchema,
});

export const DeviceMetricGetAllResponseSchema = PaginatedBundleSchema.extend({
  deviceMetrics: z.array(DeviceMetricGetResponseSchema),
});

export const DeviceMetricGetAllParamsSchema = PaginationSchema.extend({
  id: UUIDSchema,
  category: DeviceMetricCategorySchema.optional(),
  type: DeviceMetricTypeSchema.optional(),
});

export enum PatchPostureCalibrateValue {
  /**
   * Sitting upright
   */
  Upright = 'Upright',
  /**
   * The supine position means lying horizontally with the face and torso facing up
   */
  LayingSupine = 'LayingSupine',
}
export const PatchPostureCalibrateValueSchema = z.nativeEnum(
  PatchPostureCalibrateValue,
);

interface BaseCommandRequest<TCommand extends Command, TData> {
  type: TCommand;
  deviceName: string;
  data: TData;
}

/**
 * Patch calibration can contain any combination of posture and temperature values
 */
const PatchCalibrateDataSchema = z.object({
  patchId: z.string(),
  posture: PatchPostureCalibrateValueSchema.optional(),
  temperature: z.string().optional(),
  easyFall: z.boolean().optional(),
});

export type PatchCalibrateCommandRequest = BaseCommandRequest<
  Command.PatchCalibrate,
  PatchCalibrateData
>;

/**
 * Union of all possible device commands
 */
export type DeviceCommandRequest = PatchCalibrateCommandRequest;

export type DeviceCommand = z.infer<typeof DeviceCommandSchema>;
export type DeviceMetric = z.infer<typeof DeviceMetricSchema>;
export type DeviceMetricGetAllParams = z.infer<
  typeof DeviceMetricGetAllParamsSchema
>;
export type DeviceMetricGetAllResponse = z.infer<
  typeof DeviceMetricGetAllResponseSchema
>;
export type PatchCalibrateData = z.infer<typeof PatchCalibrateDataSchema>;
export type DeviceMetricGetResponse = z.infer<
  typeof DeviceMetricGetResponseSchema
>;
